import { motion, AnimatePresence } from 'framer-motion'
import { Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { useDebounce } from 'src/shared/hooks/use-debounce'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

import SearchContent from './search/search-content'

type SearchbarProps = {
  placeholder?: string
  className?: string
  textClassName?: string
  iconClassName?: string
  variant?: 'filters' | 'user-search'
}

const Searchbar = ({
  placeholder = 'Search for professional',
  className,
  textClassName,
  iconClassName,
  variant = 'user-search',
}: SearchbarProps) => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const {
    value: searchText,
    debouncedValue: debouncedQuery,
    setValue: setSearchText,
  } = useDebounce<string>('', {
    delay: 300,
  })

  const searchBarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setOpen])

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (variant === 'filters') {
      if (isMobile) {
        navigate(paths.marketplace.filters.getHref())
      } else {
        navigate(paths.marketplace.getHref())
      }
    }
  }

  return (
    <>
      <div
        className={cn(
          'relative flex h-full min-h-[50px] min-w-[250px] flex-1 flex-row items-center gap-2.5 rounded-full border border-border bg-white px-4 py-2 hover:border-primary sm:min-w-[400px] sm:flex-auto md:py-3 lg:min-w-[250px] xl:min-w-[300px]',
          className,
          variant === 'filters' && 'cursor-pointer',
        )}
        ref={searchBarRef}
        onClick={handleClick}
      >
        <Search className={cn('size-[20px] min-h-[20px] min-w-[20px] text-primary', iconClassName)} />
        <input
          className={cn(
            'min-w-[180px] flex-1 text-ellipsis whitespace-nowrap bg-white text-[15px] leading-[100%] text-primary outline-none placeholder:text-[15px] placeholder:text-primary md:min-w-[200px]',
            textClassName,
            variant === 'filters' && 'pointer-events-none cursor-pointer',
          )}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value)
            setOpen(e.target.value.length > 0)
          }}
          onFocus={() => setOpen(debouncedQuery.length > 0)}
          placeholder={placeholder}
          onClick={handleClick}
        />
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 top-full z-10 mt-3 h-[350px] max-h-[350px] w-[400px] overflow-hidden rounded-3xl border bg-background shadow-xl"
            >
              <div className="relative  h-[350px] max-h-[350px] w-[400px] p-5 pb-0">
                <SearchContent query={debouncedQuery} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}

export default Searchbar
